import { useEffect, useRef } from 'react';
import { Form, Input } from 'antd';
import type { FormInstance } from 'antd';
import { useStoreSelection } from '@/hooks/useStoreSelection';
import { pickupStoreRules } from '../CustomerSidebar/customerFormSchema';
import { FulfillmentBar } from './FulfillmentBar';
import {
  effectiveShippingMode,
  fromChoice,
  toChoice,
  type FulfillmentChoice,
  type ReceiveMethodValue,
  type ShippingModeValue,
} from './fulfillmentChoice';

/** 16 field vận chuyển sống trong POS Form — nguồn sự thật duy nhất của đơn. */
export const SHIPPING_FORM_FIELDS = [
  'shipping_mode',
  'shipping_fee_payer',
  'shipping_fee',
  'prepaid_amount',
  'debt_amount',
  'shipping_partner',
  'shipping_note',
  'shipping_label',
  'shipping_promo_code',
  'package_weight',
  'package_length',
  'package_width',
  'package_height',
  /**
   * Đơn NÀY đã từng có giá trị Thông tin gói hàng (mặc định hoặc thu ngân tự
   * nhập). `POSPage` chỉ điền `DEFAULT_PACKAGE_INFO` đúng MỘT LẦN khi cờ này
   * chưa bật — bật rồi thì không effect nào được phép tự ghi đè bốn field
   * `package_*` nữa, kể cả khi thu ngân xoá sạch cả bốn ô. Xem
   * `TCR-docs/superpowers/specs/2026-08-06-pos-package-info-defaults-design.md`.
   */
  'package_info_initialized',
  'shipping_insurance_enabled',
  'shipping_insurance_value',
] as const;

/**
 * Field KHÔNG thuộc nhóm vận chuyển nhưng wrapper này đọc/ghi, nên cũng phải do
 * nó đăng ký — antd chỉ theo dõi field ĐANG MOUNT. Tách khỏi
 * `SHIPPING_FORM_FIELDS` để danh sách 16 field vận chuyển giữ đúng ý nghĩa.
 */
const FULFILLMENT_OWNED_FIELDS = ['receive_method', 'pickup_store_id'] as const;

/** Mọi field wrapper chịu trách nhiệm đăng ký vào Form. */
export const FULFILLMENT_REGISTERED_FIELDS = [
  ...FULFILLMENT_OWNED_FIELDS,
  ...SHIPPING_FORM_FIELDS,
] as const;

/**
 * Field wrapper đăng ký bằng `Form.Item` ẨN = tất cả TRỪ `pickup_store_id` và
 * TRỪ `shipping_note`.
 *
 * `pickup_store_id` đã có Form.Item riêng tuỳ lựa chọn: `vault` dùng ô HIỂN THỊ
 * bên trong `FulfillmentBar` (`BranchSelectField`, kèm `rules` required); `pickup`
 * dùng Form.Item ẨN ngay dưới (thân component, ngoài mảng này) — hai lựa chọn loại
 * trừ nhau nên KHÔNG BAO GIỜ cả hai cùng render. Thêm `pickup_store_id` vào mảng
 * này (đăng ký ẩn VÔ ĐIỀU KIỆN, không tuỳ `choice`) là dựng lại chính bug TCR-21 —
 * hai `Form.Item` trùng tên trên cùng màn hình sinh hai phần tử cùng
 * `id="pickup_store_id"` và hai dòng lỗi khi validate fail. Cả cơ chế
 * `showBranchField` đã bị xoá tồn tại chỉ vì bug đó.
 *
 * `shipping_note` (TCR-2451) cũng không nằm ở đây: ở chế độ TỰ vận chuyển nó là
 * ô nhập THẬT (xem `ShippingNoteField` bên dưới), ở chế độ ĐVVC nó nằm trong
 * `CarrierPanel`. Render nó ẩn ở MỌI chế độ rồi thêm một ô nhập nữa ở chế độ
 * tự vận chuyển ⇒ đúng bug TCR-21 lặp lại (hai `Form.Item` trùng tên ⇒ hai ô
 * cùng `id`, hai dòng lỗi validate). Nên ở đây nó được render ẨN *hoặc* HIỂN
 * THỊ, không bao giờ cả hai.
 *
 * Không đăng ký ẩn KHÔNG làm mất dữ liệu: mọi nơi đọc `pickup_store_id`
 * (`useMissingCustomerFields`, `buildCreateOrderPayload`, snapshot tab) đều đọc
 * bằng `getFieldsValue(true)` / `useWatch(..., { preserve: true })`, tức đọc cả
 * field không mount.
 *
 * ⚠️ ĐỪNG "sửa lỗi thiếu field" bằng cách bỏ `.filter(...)` để render lại đủ 18
 * item ẩn (`FULFILLMENT_REGISTERED_FIELDS` = 2 field wrapper + 16 field vận
 * chuyển). Ở chế độ giao hàng `pickup_store_id` KHÔNG có Form.Item nào là ĐÚNG
 * (đơn giao hàng không có chi nhánh nhận); ở chế độ pickup/vault nó có đúng một
 * item — và phải giữ đúng một. Guard: `POSPage.fulfillmentLayout.test.tsx` đếm
 * `document.querySelectorAll('#pickup_store_id')` phải bằng 1.
 */
const HIDDEN_REGISTERED_FIELDS = FULFILLMENT_REGISTERED_FIELDS.filter(
  (name) => name !== 'pickup_store_id' && name !== 'shipping_note',
);

/**
 * Bốn ô "Thông tin gói hàng" + cờ khởi tạo TUYỆT ĐỐI không nằm trong hai hàm
 * reset bên dưới.
 *
 * Kích thước và khối lượng là thuộc tính của CHÍNH KIỆN HÀNG thu ngân vừa cân,
 * không phải của chế độ giao. Thu ngân cân gói ở "Giao cho ĐVVC", ghé "Tự vận
 * chuyển"/"Lấy tại chi nhánh" xem thử rồi quay lại — số đo phải còn nguyên,
 * không ai cân lại lần hai. Reset chúng chính là bug đã sửa: mọi giá trị tự
 * nhập bị đẩy về `DEFAULT_PACKAGE_INFO` chỉ vì thu ngân bấm nhầm một nút.
 *
 * Để lại trong Form khi đơn không ở chế độ ĐVVC KHÔNG rò ra BE:
 * `buildShippingPayload` chỉ gửi bốn field này ở nhánh `mode === 'carrier'`.
 *
 * `package_info_initialized` cũng phải ở ngoài: bật oan lúc rời chế độ giao
 * hàng sẽ chặn `POSPage` điền mặc định ở lần ĐẦU vào ĐVVC ⇒ bốn ô trống trơn.
 * Không đụng tới nó thì cờ giữ đúng ngữ nghĩa "đơn này đã từng có số đo chưa".
 */

/** Giá trị đưa vào Form khi đơn KHÔNG còn là giao hàng. */
export function shippingResetValues(): Record<string, unknown> {
  return {
    shipping_mode: undefined,
    shipping_fee_payer: 'seller',
    shipping_fee: 0,
    prepaid_amount: 0,
    debt_amount: 0,
    shipping_partner: undefined,
    shipping_note: '',
    shipping_label: undefined,
    shipping_promo_code: '',
    shipping_insurance_enabled: false,
    shipping_insurance_value: undefined,
  };
}

/**
 * Chuyển từ "Giao cho ĐVVC" sang "Tự vận chuyển": bỏ dữ liệu chỉ ĐVVC cần,
 * GIỮ phí/người trả phí/trả trước vì tự vận chuyển vẫn dùng đúng ba số đó.
 *
 * TCR-2451 — `shipping_note` KHÔNG còn bị xoá ở đây. Ghi chú vận chuyển là
 * thông tin của NGƯỜI NHẬN (giao trước mấy giờ, gọi trước khi đến…), không phải
 * thông tin riêng của hãng vận chuyển: đơn TỰ vận chuyển cũng cần, và đã có ô
 * nhập riêng (`ShippingNoteField` trong file này). Xoá nó ở đây là mất thông
 * mỗi lần thu ngân đổi qua lại giữa hai chế độ để xem giá — thao tác rất hay
 * xảy ra lúc chốt đơn. `shippingResetValues()` (đơn rời hẳn khỏi giao hàng)
 * vẫn xoá: lúc đó đơn KHÔNG giao nữa thì ghi chú giao hàng là rác.
 */
export function carrierResetValues(): Record<string, unknown> {
  return {
    shipping_partner: undefined,
    shipping_label: undefined,
    shipping_promo_code: '',
    shipping_insurance_enabled: false,
    shipping_insurance_value: undefined,
  };
}

/**
 * Ô "Ghi chú vận chuyển" cho đơn TỰ vận chuyển (TCR-2451).
 *
 * Trước TCR-2451 ô này CHỈ tồn tại trong `CarrierPanel` (chế độ Giao cho ĐVVC).
 * Đơn tự vận chuyển — cửa hàng tự chở — vẫn cần ghi chú kiểu "giao trước 17h
 * ngày 25/10", "gọi trước khi đến", nhưng thu ngân không có chỗ nào để ghi, và
 * ghi chú đó cũng không bao giờ được in ra PXK. Ở chế độ ĐVVC ô vẫn nằm trong
 * `CarrierPanel` — hai ô KHÔNG BAO GIỜ cùng hiện (xem `showShippingNoteField`).
 *
 * `maxLength` khớp BE (`orders.shipping_note` — 2000 ký tự): ô nhận cho phép
 * gõ nhiều hơn thì BE chặn cả lần ghi đơn, thu ngân không hiểu vì sao.
 */
export function ShippingNoteField(): JSX.Element {
  return (
    <Form.Item
      name="shipping_note"
      label="Ghi chú vận chuyển"
      style={{ marginBottom: 8 }}
    >
      <Input.TextArea
        rows={2}
        maxLength={2000}
        aria-label="Ghi chú vận chuyển"
        placeholder="VD: Giao trước 17h ngày 25/10"
      />
    </Form.Item>
  );
}

interface Props {
  form: FormInstance;
  /**
   * `pos_default_fulfillment_choice` (Settings → POS) — dùng để `toChoice` biết
   * nút nào sáng khi đơn giao hàng chưa có `shipping_mode` (đơn mới, thu ngân
   * chưa bấm gì). Cùng nguồn với `effectiveShippingMode` ở biên đọc payload
   * (`buildCreateOrderPayload`) — một cấu hình, một hành vi nhất quán trên cả
   * hai biên.
   */
  defaultFulfillmentChoice: FulfillmentChoice;
}

/**
 * Cầu nối DUY NHẤT giữa POS Form và thanh phương thức nhận hàng. Mọi component
 * khác trong thư mục này thuần trình bày — muốn test chúng không cần dựng Form.
 */
export function FulfillmentFormSection({ form, defaultFulfillmentChoice }: Props): JSX.Element {
  const receiveMethod =
    (Form.useWatch('receive_method', form) as ReceiveMethodValue | undefined) ?? 'delivery';
  const shippingMode = Form.useWatch('shipping_mode', form) as ShippingModeValue | undefined;
  const choice = toChoice(
    { receive_method: receiveMethod, shipping_mode: shippingMode },
    defaultFulfillmentChoice,
  );

  /**
   * Chế độ vận chuyển HIỆU LỰC — KHÔNG phải `shipping_mode` thô.
   *
   * Đơn giao hàng mà `shipping_mode` còn rỗng (đơn mới, thu ngân chưa bấm nút
   * nào) phải được coi là 'self' — cùng luật với `toChoice` ở trên và với
   * `normalizedShippingMode` bên `POS/index.tsx` (nơi quyết định hiện cột ĐVVC).
   * Dùng `shipping_mode` thô ở đây là lệch: ô ghi chú và cột ĐVVC sẽ hiện
   * cùng lúc (ĐVVC có ô ghi chú riêng trong `CarrierPanel`) — đúng cái lỗi
   * "hai ô cùng lúc" mà TCR-2451 cấm.
   */
  const effectiveMode = effectiveShippingMode(
    receiveMethod,
    shippingMode,
    defaultFulfillmentChoice,
  );
  /**
   * Ô ghi chú vận chuyển chỉ hiện ở chế độ TỰ vận chuyển. Ở chế độ ĐVVC ô nằm
   * trong `CarrierPanel`; ở chế độ lấy tại chi nhánh / lưu kho thì đơn không
   * giao hàng ⇒ không có ghi chú giao hàng nào để ghi.
   */
  const showShippingNoteField = effectiveMode === 'self';

  const { currentStore } = useStoreSelection();
  const sellingStoreId = currentStore?.storeId;

  // Task 7b: effect chuẩn hoá `shipping_mode` (không deps, chạy mỗi render) đã bị
  // XOÁ khỏi đây. Bất biến "đơn giao hàng thiếu shipping_mode = coi như 'self'"
  // giờ do BIÊN ĐỌC giữ (`effectiveShippingMode` trong `fulfillmentChoice.ts`,
  // dùng ở `missingCustomerFields` và `buildCreateOrderPayload`) — không còn ai
  // cần GHI field này vào Form chỉ để giữ bất biến đó. Lý do effect cũ mong manh:
  // nó không có deps vì `POSPage` gọi `resetFields()` trong effect CHA chạy SAU
  // effect CON này, nên bản ghi 'self' lúc mount luôn bị xoá rồi phải chạy lại ở
  // lần render kế — thêm deps vào là bug (dead-end ở `Orders/edit`) quay lại.
  // Chuẩn hoá ở biên đọc không phụ thuộc thứ tự effect nên không còn mong manh đó.

  // Đồng bộ MỘT CHIỀU `shiftStoreId → pickup_store_id` cho `vault`. Chiều ngược
  // lại KHÔNG tồn tại: `pickup_store_id` là nơi KHÁCH nhận hàng, `shiftStoreId` là
  // chi nhánh THU NGÂN đang mở ca — ghi ngược sẽ chuyển cả ca bán hàng sang chi
  // nhánh khác, đơn ghi sai cửa hàng và tồn kho trừ nhầm.
  //
  // Effect này CHỈ còn áp dụng cho `vault` (đã ẩn khỏi thanh 4 nút, chỉ đơn khôi
  // phục cũ còn mang giá trị này) — nó vẫn giữ ô chọn tay `BranchSelectField` nên
  // phải giữ nguyên tắc cũ: chỉ ghi khi chi nhánh đang bán THẬT SỰ đổi, không ghi
  // đè giá trị khách/tab đã khôi phục. `pickup` giờ có effect riêng NGAY DƯỚI ĐÂY —
  // luôn ép giá trị, không còn khái niệm "chọn tay" để giữ nữa.
  //
  // `useRef` giữ chi nhánh đã đồng bộ lần trước để chỉ ghi khi nó THẬT SỰ đổi —
  // lần đầu biết chi nhánh đang bán (kể cả khi danh sách store load async) chỉ ghi
  // nhận, không ghi vào form: giá trị đang có có thể là chi nhánh khách đã chọn ở
  // đơn cũ / snapshot tab. Nhờ vậy thu ngân sửa tay xong vẫn giữ được giá trị.
  const syncedStoreRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!sellingStoreId) return;
    const previous = syncedStoreRef.current;
    syncedStoreRef.current = sellingStoreId;
    if (previous === undefined || previous === sellingStoreId) return;
    const methodNow = form.getFieldValue('receive_method') as ReceiveMethodValue | undefined;
    if (methodNow !== 'vault') return;
    form.setFieldValue('pickup_store_id', sellingStoreId);
  }, [sellingStoreId, form]);

  // `pickup` không còn dropdown để thu ngân tự chọn — chi nhánh nhận hàng LUÔN LÀ
  // chi nhánh đang bán, ép mọi lúc: lúc mount (kể cả với giá trị cũ khôi phục từ
  // tab), lúc đổi tab về pickup, và lúc chi nhánh đang bán đổi trong khi đơn đang
  // ở pickup. Khác hẳn effect vault ở trên — không có `useRef`/"chỉ ghi khi đổi",
  // vì pickup không còn khái niệm giá trị chọn tay cần giữ.
  useEffect(() => {
    if (receiveMethod !== 'pickup') return;
    if (!sellingStoreId) return;
    if (form.getFieldValue('pickup_store_id') === sellingStoreId) return;
    form.setFieldValue('pickup_store_id', sellingStoreId);
  }, [receiveMethod, sellingStoreId, form]);

  const handleChange = (next: FulfillmentChoice) => {
    const fields = fromChoice(next);
    const patch: Record<string, unknown> = {
      receive_method: fields.receive_method,
      shipping_mode: fields.shipping_mode,
    };

    if (fields.receive_method !== 'delivery') {
      // Đổi khỏi giao hàng phải dọn cả nhóm vận chuyển, nếu không đơn pickup vẫn
      // mang theo shippingMode/phí và bị đẩy job tạo vận đơn oan.
      Object.assign(patch, shippingResetValues(), {
        receive_method: fields.receive_method,
        shipping_mode: undefined,
        pickup_store_id: sellingStoreId,
      });
    } else if (shippingMode === 'carrier' && fields.shipping_mode === 'self') {
      Object.assign(patch, carrierResetValues(), { shipping_mode: 'self' });
    }

    form.setFieldsValue(patch);
  };

  return (
    <>
      {/* Đăng ký field để Form.useWatch/getFieldsValue thấy chúng dù không có
          input hiển thị — antd chỉ theo dõi field ĐANG MOUNT. */}
      {HIDDEN_REGISTERED_FIELDS.map((name) => (
        <Form.Item key={name} name={name} hidden>
          <Input />
        </Form.Item>
      ))}

      {/* `pickup` đăng ký `pickup_store_id` bằng Form.Item ẨN riêng — không dùng
          `BranchSelectField` như `vault`. Giữ `rules` required để `validateFields()`
          vẫn có lưới an toàn (phòng hờ trường hợp hiếm chưa biết chi nhánh đang
          bán), dù effect ở trên gần như luôn điền sẵn giá trị trước khi thu ngân
          bấm ghi hoá đơn. KHÔNG bao giờ trùng với ô của `vault` vì hai lựa chọn
          loại trừ nhau — không tái lập bug TCR-21 (hai Form.Item cùng tên). */}
      {choice === 'pickup' && (
        <Form.Item name="pickup_store_id" hidden rules={pickupStoreRules}>
          <Input />
        </Form.Item>
      )}

      <FulfillmentBar value={choice} onChange={handleChange} />

      {/*
        TCR-2451 — ô ghi chú vận chuyển của đơn TỰ vận chuyển.

        Ở chế độ ĐVVC (và ở mọi chế độ không giao hàng) ô này vẫn phải được ĐĂNG KÝ
        vào Form — chỉ là ẩn. Lý do: `POS/index.tsx` đọc `shipping_note` bằng
        `Form.useWatch('shipping_note', form)` (KHÔNG có `preserve: true`) để đổ
        vào `CarrierPanel`; antd chỉ theo dõi field ĐANG MOUNT, không có
        `Form.Item` nào thì `useWatch` luôn trả `undefined` ⇒ ô ghi chú trong
        `CarrierPanel` mất sạch nội dung mỗi lần đổi chế độ.

        Render ẩn ở mọi chế độ + render hiển thị ở chế độ tự vận chuyển là HAI
        `Form.Item` cùng tên nếu không loại trừ cẩn thận — đúng bug TCR-21 (hai
        ô cùng `id`, hai dòng lỗi validate). Ở đây hai nhánh là `?:` loại trừ
        nhau: chỉ một `Form.Item` `shipping_note` tồn tại tại mỗi thời điểm.
      */}
      {showShippingNoteField ? (
        // `flexShrink: 0` + padding: khối này nằm ở đáy cột hàng (flex column,
        // `overflow: hidden`) — không khoá lại thì nó bị bóp còn 0 px khi màn
        // thấp, tức ô nhập biến mất đúng lúc thu ngân cần nó nhất.
        <div
          className="pos-shipping-note-slot"
          style={{
            flexShrink: 0,
            borderTop: '1px solid #e0e0e0',
            padding: '8px 12px',
          }}
        >
          <ShippingNoteField />
        </div>
      ) : (
        <Form.Item name="shipping_note" hidden>
          <Input />
        </Form.Item>
      )}
    </>
  );
}
